import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Grid,
  Chip,
  Stack,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import { EventAvailable, School } from '@mui/icons-material';
import { Update } from '@mui/icons-material';
import { Person } from '@mui/icons-material';
import { Groups } from '@mui/icons-material';

const Events = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const getText = (nlText: string, enText: string) => {
    return language === 'nl' ? nlText : enText;
  }
  
  const handleCustomEventClick = () => {
      navigate('/contact', { 
          state: { 
              prefill: { 
                  subject: language === 'nl' ? 'Aanvraag Maatwerk Event' : 'Custom Event Request' 
              } 
          } 
      });
  };

  return (
    <>
      <PageHeader
        title={getText('Evenementen & Workshops', 'Events & Workshops')}
        subtitle={
          getText('Blijf op de hoogte van inspirerende sessies, trainingen en lezingen.',
            'Stay tuned for inspiring sessions, trainings, and lectures.')
        }
      />
      
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        {/* --- Coming Soon Section --- */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, md: 5 }, 
            textAlign: 'center', 
            mb: 6, 
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <EventAvailable sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
            {getText('Binnenkort Beschikbaar!', 'Coming Soon!')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '700px', mx: 'auto' }}>
            {getText(
              'Er worden momenteel nieuwe inspirerende workshops en cursussen ontwikkeld om jou verder te helpen in je persoonlijke en professionele groei. Houd deze pagina in de gaten voor updates!',
              'New inspiring workshops and courses are currently being developed to help you further in your personal and professional growth. Keep an eye on this page for updates!'
            )}
          </Typography>
           <Chip 
             icon={<Update />} 
             label={getText("Updates volgen spoedig", "Updates coming soon")} 
             variant="outlined"
             color="primary"
             sx={{ fontWeight: 500 }}
           />
        </Paper>

        {/* --- Mention Specific/Past Events (Example: WUR) --- */}
        <Box sx={{ mb: 6, textAlign: 'center', p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
            <School sx={{ fontSize: 30, color: 'primary.main', mb: 1 }} />
             <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                {getText(
                    'Naast open workshops verzorg ik ook trainingen op maat voor organisaties en onderwijsinstellingen.',
                    'In addition to open workshops, I also provide custom training programs for organizations and educational institutions.'
                )}
            </Typography>
        </Box>

        {/* --- Nudge Section for Individual Coaching (Existing, can be kept or enhanced) --- */}
        <Paper 
          elevation={2} 
          sx={{ 
            mt: 6, // Adjusted margin top
            p: 4, 
            textAlign: 'center', 
            bgcolor: 'primary.light',
            color: 'white',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
            {getText('Kun je niet wachten of zoek je iets persoonlijkers?', 'Can\'t wait or looking for something more personal?')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}>
            {getText(
              'Overweeg dan persoonlijke 1-op-1 loopbaancoaching. Samen werken we op jouw tempo aan jouw specifieke doelen, precies wanneer het jou uitkomt.',
              'Consider personalized 1-on-1 career coaching. We\'ll work together at your pace on your specific goals, exactly when it suits you.'
            )}
          </Typography>
          <Button
            variant="contained"
            color="secondary" 
            size="large"
            startIcon={<Person sx={{ color: 'white' }} />} // Added icon
            onClick={() => navigate('/diensten/individuele-coaching')}
            sx={{ 
              px: 4, 
              py: 1.2, 
              color: 'white', 
              '&:hover': { backgroundColor: 'secondary.dark' }
            }}
          >
            {getText('Ontdek Individuele Coaching', 'Discover Individual Coaching')}
          </Button>
        </Paper>
        
        {/* --- Custom Event CTA --- */}
        <Paper 
            elevation={2} 
            sx={{ 
              mt: 4, 
              p: 4, 
              textAlign: 'center', 
              bgcolor: 'secondary.light', // Use secondary light for variation
              color: 'white', 
              borderRadius: 2
            }}
          >
             <Groups sx={{ fontSize: 40, color: 'white', mb: 1 }} />
            <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
              {getText('Workshop op Maat Nodig?', 'Need a Custom Workshop?')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}>
              {getText(
                'Op zoek naar een specifieke training of workshop voor jouw team, studentenvereniging of organisatie? Laten we samen een programma op maat maken!',
                'Looking for a specific training or workshop for your team, student association, or organization? Let\'s create a custom program together!'
              )}
            </Typography>
            <Button
              variant="contained"
              color="primary" // Use primary for contrast on secondary background
              size="large"
              startIcon={<ContactSupportIcon />} // Added icon
              onClick={handleCustomEventClick} // Reusing the existing handler
              sx={{ 
                px: 4, 
                py: 1.2, 
                color: 'white', 
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              {getText('Vraag Maatwerk Aan', 'Request Custom Event')}
            </Button>
          </Paper>

      </Container>
    </>
  );
};

export default Events; 